import type { PostHog } from "posthog-js/dist/module.full.no-external";

// Optional PostHog error tracking and session replay for hosted testing. It
// starts only when the server supplies a project key, loads lazily so the
// workspace never waits on it, and every call is best-effort.
export type AnalyticsConfig = { key: string; host: string };
let client: Promise<PostHog | undefined> | undefined;

export function startProductAnalytics(config?: AnalyticsConfig | null) {
  if (
    client ||
    !config?.key ||
    // The headless agent renderer runs server-side; never record it.
    new URLSearchParams(location.search).get("agentRender") === "1"
  )
    return;
  // The full no-external bundle includes the recorder and exception capture,
  // so no third-party script is fetched at runtime.
  client = import("posthog-js/dist/module.full.no-external")
    .then(({ default: posthog }) => {
      posthog.init(config.key, {
        // Same-origin Worker proxy: ad blockers would otherwise drop testers.
        api_host: `${location.origin}/ingest`,
        ui_host: config.host.replace(".i.posthog.com", ".posthog.com"),
        defaults: "2026-08-30",
        person_profiles: "identified_only",
        capture_pageview: "history_change",
        capture_exceptions: true,
        // Links API failures in the Worker to this browser session.
        tracing_headers: [location.hostname],
        session_recording: {
          maskAllInputs: false,
          maskInputOptions: { password: true },
        },
      });
      posthog.register({
        $app_version:
          typeof __PLANROOM_BUILD__ === "string"
            ? __PLANROOM_BUILD__
            : "development",
      });
      return posthog;
    })
    .catch(() => undefined);
}

function use(action: (posthog: PostHog) => void) {
  void client
    ?.then((posthog) => posthog && action(posthog))
    .catch(() => {});
}
export function identifyPerson(
  user: { id: string; name: string },
  email?: string,
) {
  use((posthog) => posthog.identify(user.id, { name: user.name, email }));
}
export function forgetPerson() {
  use((posthog) => posthog.reset());
}
// Conversion events only: no board content, document text or file names.
export function captureEvent(
  name: "sign_up_started" | "signed_up" | "project_created" | "invite_accepted",
  properties: Record<string, string | number | boolean> = {},
) {
  use((posthog) => posthog.capture(name, properties));
}
export function captureError(
  error: unknown,
  properties: Record<string, unknown> = {},
) {
  use((posthog) => posthog.captureException(error, properties));
}
